import { FC, useEffect, useState } from 'react'

import styled from 'styled-components'
import { useForm, Controller } from 'react-hook-form'

import { Button } from 'src/Components/Button'
import { Input } from 'src/Components/Input/index.tsx'
import { Select } from 'src/Components/Select'
import { Modal } from 'src/Components/Modal'
import { colors } from 'src/Helpers/constants.ts'
import {
  ManagedUser,
  UpdateUserBody,
  UserRole,
} from 'src/Redux/api/usersApi.ts'

import { assignableRoleOptions } from './constants.ts'

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 0;
  border-bottom: 1px solid ${colors.mainGray};
`

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  color: ${colors.thirdBlue};
`

const Row = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  width: 100%;
`

const emailPattern = {
  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  message: 'Некорректный формат email',
}

interface EditUserModalProps {
  onClose: () => void
  isOpened: boolean
  userToEdit: ManagedUser | null
  onUpdateUser: (id: number, data: UpdateUserBody) => void
  onChangeRole: (id: number, role: UserRole) => void
  onResetPassword: (id: number, password: string) => void
}

export const EditUserModal: FC<EditUserModalProps> = ({
  isOpened,
  onClose,
  userToEdit,
  onUpdateUser,
  onChangeRole,
  onResetPassword,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UpdateUserBody>({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
    },
  })

  const [role, setRole] = useState<UserRole>(UserRole.SALES)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')

  useEffect(() => {
    if (userToEdit) {
      reset({
        email: userToEdit.email,
        firstName: userToEdit.firstName || '',
        lastName: userToEdit.lastName || '',
        phone: userToEdit.phone || '',
      })
      setRole(userToEdit.role)
      setPassword('')
      setPasswordError('')
    }
  }, [userToEdit, reset])

  if (!userToEdit) {
    return null
  }

  const isAdminUser = userToEdit.role === UserRole.ADMIN

  const onSubmitInfo = (data: UpdateUserBody): void => {
    onUpdateUser(userToEdit.id, data)
  }

  const onSubmitRole = (): void => {
    onChangeRole(userToEdit.id, role)
  }

  const onSubmitPassword = (): void => {
    if (password.length < 6) {
      setPasswordError('Минимум 6 символов')
      return
    }
    onResetPassword(userToEdit.id, password)
    setPassword('')
    setPasswordError('')
  }

  return (
    <Modal opened={isOpened} onClose={onClose} title='Изменить пользователя'>
      <Section>
        <SectionTitle>Личные данные</SectionTitle>
        <Controller
          name='email'
          control={control}
          rules={{ required: 'Email обязателен', pattern: emailPattern }}
          render={({ field }) => (
            <Input
              placeholder='example@company.com'
              label='Email'
              error={errors.email?.message}
              {...field}
            />
          )}
        />
        <Controller
          name='firstName'
          control={control}
          render={({ field }) => (
            <Input placeholder='Имя' label='Имя' {...field} />
          )}
        />
        <Controller
          name='lastName'
          control={control}
          render={({ field }) => (
            <Input placeholder='Фамилия' label='Фамилия' {...field} />
          )}
        />
        <Controller
          name='phone'
          control={control}
          render={({ field }) => (
            <Input placeholder='Телефон' label='Телефон' {...field} />
          )}
        />
        <Button onClick={handleSubmit(onSubmitInfo)}>Сохранить данные</Button>
      </Section>

      <Section>
        <SectionTitle>Роль</SectionTitle>
        <Row>
          <Select
            label='Роль'
            data={assignableRoleOptions}
            value={role}
            disabled={isAdminUser}
            onChange={(value) => value && setRole(value as UserRole)}
          />
          <Button color='gray' disabled={isAdminUser} onClick={onSubmitRole}>
            Изменить роль
          </Button>
        </Row>
      </Section>

      <Section>
        <SectionTitle>Сброс пароля</SectionTitle>
        <Row>
          <Input
            placeholder='Новый пароль'
            label='Новый пароль'
            type='password'
            value={password}
            error={passwordError}
            onChange={(event) => setPassword(event.target.value)}
          />
          <Button color='red' variant='light' onClick={onSubmitPassword}>
            Сбросить пароль
          </Button>
        </Row>
      </Section>
    </Modal>
  )
}
